// MultipleChartsView V2 - обновлённая версия с UnifiedChartRenderer
// Заменяет: ChartDisplayManager + MultiChartRenderer на новую архитектуру
// Архитектура: ChartLayerManager + UnifiedChartRenderer + CSS переключения

import { devError } from '../../../utils/devLog';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, useToast } from '@chakra-ui/react';
import { useMultipleCharts } from '../../common/AstrologyChart/MultipleChartsContext';
import ChartTabs from '../../common/AstrologyChart/ChartTabs';
import ChartLayerManager from '../../common/AstrologyChart/ChartLayerManager';
import ChartManagement from '../../common/AstrologyChart/ChartManagement';
import { WorkspacePersistenceProvider } from '../../common/AstrologyChart/WorkspacePersistence';
import WorkspaceStatusIndicator from '../../common/AstrologyChart/WorkspaceStatusIndicator';
import { PerformanceManagerProvider } from '../../common/AstrologyChart/PerformanceManager';
import LoadModal from '../storage/LoadModal';
import { createChartManager, ToastConfig } from '../../../services/ChartManager';
import { ChartMetadata } from '../../../models/storage';
import { SharedChartData, SharedViewMode } from '../../../types/share.types';
import { jatakaResponseToChartData } from '../../../utils/apiDataTransformers';

interface MultipleChartsViewProps {
  className?: string;
  showDetails?: boolean;
  detailsPosition?: 'right' | 'bottom';
  enableAnimations?: boolean;
  debugMode?: boolean;
  onChartLoad?: (chartId: string) => void;
  onChartUnload?: (chartId: string) => void;
  onWorkspaceChange?: () => void;
  shareMode?: boolean;  // If true, disable tab creation/closing
  sharedChartData?: SharedChartData;  // Chart data for share mode (all sections always visible)
  sharedViewMode?: SharedViewMode;  // View mode for shared charts (author/guest)
}

/**
 * Контейнер множественных чартов с новой архитектурой
 */
const MultipleChartsContainer: React.FC<MultipleChartsViewProps> = ({
  className,
  showDetails = true,
  detailsPosition = 'right',
  enableAnimations = true,
  debugMode = false,
  onChartLoad,
  onChartUnload,
  onWorkspaceChange,
  shareMode = false,
  sharedChartData,
  sharedViewMode = 'author'
}) => {
  const {
    charts,
    activeChartId,
    loadedChartIds,
    isLoading,
    loadChart,
    loadChartFromData,
    switchToChart,
    closeTab,
    workspace
  } = useMultipleCharts();

  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showChartManagement, setShowChartManagement] = useState(false);
  const [isSharedChartLoaded, setIsSharedChartLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Create ChartManager instance
  const chartManager = useMemo(() => {
    const showToastHandler = (config: ToastConfig) => {
      toast({
        ...config,
        isClosable: true
      });
    };

    // Callback to get content hashes of loaded charts for duplicate detection
    const getLoadedContentHashes = (): Map<string, string> => {
      const hashes = new Map<string, string>();
      for (const [chartId, chart] of charts) {
        if (chart.metadata.contentHash) {
          hashes.set(chartId, chart.metadata.contentHash);
        }
      }
      return hashes;
    };

    return createChartManager(
      loadChart,
      loadedChartIds,
      workspace,
      showToastHandler,
      getLoadedContentHashes
    );
  }, [loadChart, loadedChartIds, workspace, toast, charts]);

  // Отслеживаем изменения в загруженных чартах
  useEffect(() => {
    if (debugMode) {
    }
  }, [loadedChartIds, activeChartId, charts, debugMode]);

  // Load shared chart data in shareMode (once on mount)
  useEffect(() => {
    if (shareMode && sharedChartData && !isSharedChartLoaded) {

      // Safe timezone offset handling (fallback to 0 if undefined)
      const timezoneOffset = sharedChartData.chart_metadata.timezone_offset ?? 0;
      const timezoneOffsetStr = `${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset.toFixed(2)}`;

      // Reconstruct BirthData from chart_metadata
      const birthData = {
        chartName: sharedChartData.chart_metadata.name || 'Shared Chart',
        locationName: sharedChartData.chart_metadata.birth_place,
        date: new Date(sharedChartData.chart_metadata.birth_time).toLocaleDateString('en-GB').replace(/\//g, '-'),
        time: new Date(sharedChartData.chart_metadata.birth_time).toLocaleTimeString('en-GB', { hour12: false }),
        longitude: sharedChartData.chart_metadata.longitude,
        latitude: sharedChartData.chart_metadata.latitude,
        timezoneMode: 'manual' as const,
        timezoneOffset: timezoneOffsetStr,
        selectedLocation: sharedChartData.chart_metadata.birth_place
      };

      // Transform API response to ParsedChartData
      const parsedChartData = jatakaResponseToChartData(sharedChartData.chart_data, birthData);

      // Metadata for chart instance
      const metadata = {
        id: sharedChartData.short_code,
        name: sharedChartData.chart_metadata.name || 'Shared Chart',
        chartDateTime: {
          date: new Date(sharedChartData.chart_metadata.birth_time).toLocaleDateString('en-GB').replace(/\//g, '-'),
          time: new Date(sharedChartData.chart_metadata.birth_time).toLocaleTimeString('en-GB', { hour12: false }),
          location: sharedChartData.chart_metadata.birth_place,
          timezone: sharedChartData.chart_metadata.timezone,
          latitude: sharedChartData.chart_metadata.latitude,
          longitude: sharedChartData.chart_metadata.longitude
        }
      };


      loadChartFromData(sharedChartData.short_code, parsedChartData, metadata, { makeActive: true })
        .then(() => {
          setIsSharedChartLoaded(true);
        })
        .catch(error => {
          devError('❌ Failed to load shared chart:', error);
          toast({
            title: 'Error Loading Chart',
            description: 'Failed to load shared chart data',
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        });
    }
  }, [shareMode, sharedChartData, isSharedChartLoaded, loadChartFromData, toast]);

  // Обработчики для взаимодействия с чартами
  const handleChartSwitch = useCallback((chartId: string) => {
    if (chartId !== activeChartId) {
      switchToChart(chartId);
      
      if (debugMode) {
      }
    }
  }, [activeChartId, switchToChart, debugMode]);

  const handleChartRemove = useCallback(async (chartId: string) => {
    try {
      closeTab(chartId);
      
      if (onChartUnload) {
        onChartUnload(chartId);
      }
      
      toast({
        title: 'Chart Removed',
        description: 'Chart removed successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
      
      if (onWorkspaceChange) {
        onWorkspaceChange();
      }
      
    } catch (error) {
      devError('Failed to remove chart:', error);
      toast({
        title: 'Remove Error',
        description: 'Failed to remove chart',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  }, [closeTab, onChartUnload, onWorkspaceChange, toast]);

  const handleAddChart = useCallback(() => {
    (window as any).__openNewChartModal();
  }, []);

  const handleLoadChart = useCallback(async (
    chartId: string,
    metadata: ChartMetadata
  ) => {
    const result = await chartManager.loadChartFromStorage(
      chartId,
      metadata,
      { makeActive: true }
    );

    if (result.success) {
      if (onChartLoad) {
        onChartLoad(chartId);
      }

      setShowLoadModal(false);

      if (onWorkspaceChange) {
        onWorkspaceChange();
      }
    }
  }, [chartManager, onChartLoad, onWorkspaceChange]);

  // Стили контейнера
  const containerStyles = {
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    bg: 'gray.50',
    overflow: 'hidden'
  };

  const tabsContainerStyles = {
    flexShrink: 0
  };

  const displayAreaStyles = {
    flex: 1,
    position: 'relative' as const,
    overflow: 'hidden', // Изменено с 'auto' для CSS layer системы
    minHeight: 0
  };

  return (
    <Box className={className} {...containerStyles} ref={containerRef}>
      {/* Панель вкладок - скрыта в shareMode */}
      {!shareMode && (
        <Box {...tabsContainerStyles}>
          <ChartTabs
            onAddChart={handleAddChart}
            maxVisibleTabs={5}
            shareMode={shareMode}
          />
        </Box>
      )}

      {/* Область отображения чартов - новая система */}
      <Box {...displayAreaStyles} className="chart-display-area">
        <ChartLayerManager
          charts={charts}
          activeChartId={activeChartId}
          loadedChartIds={loadedChartIds}
          showDetails={showDetails}
          detailsPosition={detailsPosition}
          preloadNext={true}
          debugMode={debugMode}
          shareMode={shareMode}
          sharedViewMode={sharedViewMode}
          sharedChartData={sharedChartData}
        />
      </Box>

      {/* Модал загрузки */}
      <LoadModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        onLoadChart={handleLoadChart}
      />

      {/* Менеджер чартов */}
      <ChartManagement
        isOpen={showChartManagement}
        onClose={() => setShowChartManagement(false)}
      />
    </Box>
  );
};

/**
 * Главный компонент множественных чартов с провайдерами
 */
export const MultipleChartsView: React.FC<MultipleChartsViewProps> = (props) => {
  return (
    <WorkspacePersistenceProvider
      config={{
        enabled: !props.shareMode, // Disable persistence in share mode
        intervalMs: 30000, // 30 секунд
        saveOnTabSwitch: true,
        saveOnChartLoad: true,
        saveOnChartClose: true
      }}
      enableSessionRestore={!props.shareMode} // Disable session restore in share mode
    >
      <PerformanceManagerProvider>
        <MultipleChartsContainer {...props} />
      </PerformanceManagerProvider>

      {/* Индикатор статуса workspace - только в development */}
      {process.env.NODE_ENV === 'development' && props.debugMode && (
        <WorkspaceStatusIndicator
          position="bottom-right"
          showInDevelopment={true}
          compact={false}
        />
      )}
    </WorkspacePersistenceProvider>
  );
};

export default MultipleChartsView;